<template>
  <div class="product-group-matrix">
    <!-- Product Group Header move outside -->
    <ProductGroupMatrixHeader
      :name="productGroup.name"
      :total="productGroupTotal"
      :collapsed="collapsed"
      :productGroup="productGroup"
      :show-wholesale-price="showWholesalePrice"
      @remove-all-quantities="emit('remove-product-group-quantities')"
      @toggle-collapse="collapsed = !collapsed" />

    <div v-show="!collapsed" class="product-group-body" data-cy="product-group-size-matrix">
      <div class="matrix-table">
        <!-- Sizes Row -->
        <div class="row header matrix-table-header" :style="`top: ${headerStickyOffset}px`">
          <div class="column first-column">
            <div class="size-selector-cnt">
              <div class="size-selector">
                <div class="allways-size">{{ globalConstants.alwaysVisibleSizeType }}</div>
                <select name="size-selector" v-model="selectedSize" class="with-arrow with-focus-bg no-border">
                  <option v-for="size in filteredSizeTypes" :key="size">
                    {{ size }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="column" v-for="size in sortedProductGroupSizes" :key="size.id">
            <div class="size-cnt">
              <div
                class="always-visible-size-type"
                :style="{
                  fontSize: determineResponsiveFontSize(
                    size[globalConstants.alwaysVisibleSizeType as keyof ProductSize],
                    { ...fontSizeConfig, base: 14 },
                  ),
                }">
                {{ size[globalConstants.alwaysVisibleSizeType as keyof ProductSize] }}
              </div>
              <div
                :style="{
                  fontSize: determineResponsiveFontSize(size[selectedSize], fontSizeConfig),
                }">
                {{ size[selectedSize] }}
              </div>
            </div>
          </div>
          <div class="column last-column"></div>
        </div>

        <!-- Fabric/Variant Rows -->
        <div
          v-for="[fabricKey, fabric] in productGroup.fabrics"
          :key="fabricKey"
          class="row matrix-table-body product-fabric"
          :class="{ 'pre-order-disabled': fabric.disabled }">
          <ProductVariantDescription
            class="column first-column"
            :fabric="fabric"
            @show-product-model-details="$emit('show-product-model-details', $event)" />
          <div class="column" v-for="size in sortedProductGroupSizes" :key="size.id">
            <div :data-cy="fabric.products[size.id].availability" v-if="fabric.products[size.id]" class="product">
              <div
                v-if="fabric.products[size.id].availability"
                class="product-header"
                :class="fabric.products[size.id].availability">
                <span :title="fabric.products[size.id].availability">
                  {{
                    isASalesRep
                      ? fabric.products[size.id].availableQuantity
                      : availabilityText[fabric.products[size.id].availability!]
                  }}
                </span>
              </div>
              <div class="product-total">
                <input
                  data-cy="product-number-input"
                  :id="`product-${fabric.products[size.id].productId}`"
                  :disabled="fabric.products[size.id].disabled"
                  type="number"
                  :max="getMaxQuantity(fabric.products[size.id].productId)"
                  :min="0"
                  :value="getProductInputValue(fabric.products[size.id].productId)"
                  :placeholder="fabric.products[size.id].disabled ? '' : '0'"
                  @change="onMatrixInputChange(fabric.products[size.id], $event)" />
              </div>
            </div>
            <div v-else class="empty-cell"></div>
          </div>
          <div class="column last-column">
            <span class="strong">{{ props.fabricTotals[fabric.id] ?? 0 }}</span>
          </div>
          <div v-if="fabric.disabledReason" class="pre-order-disabled-legend">
            <p>
              {{ fabricDisabilityText[fabric.disabledReason] }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStickyHeight } from '@/composables/useStickyHeight';
import { determineResponsiveFontSize } from '@/composables/utils';
import { globalConstants } from '@/constants/global.constants';
import { ONE_SIZE_ID } from '@/constants/letter-size.constants';
import type { FontSizeConfig } from '@/models/font-size-config.interface';
import type { MatrixProduct } from '@/models/matrix-product.model';
import { useUserStore } from '@/stores/user';
import { Availability, FabricDisabledReason } from '@/types/matrix.enums';
import type { ProductGroup } from '@/types/matrix.interface';
import { sortProductSizes } from '@/utils/productSizeUtils';
import type { ProductSize } from '@onrunning/backstage-models';
import ProductGroupMatrixHeader from './ProductGroupMatrixHeaderV3.vue';
import ProductVariantDescription from './ProductVariantDescription.vue';

const MAX_QUANTITY_PER_PRODUCT = 10000;
const fontSizeConfig: FontSizeConfig = {
  base: 16,
  min: 10,
  threshold: 4,
  step: 1,
  noShrinkValues: [ONE_SIZE_ID],
  caseInsensitive: true,
};

const props = defineProps<{
  defaultSize: string;
  productGroup: ProductGroup;
  isQuantityIncreaseAllowed: boolean;
  fabricTotals: Record<string, number>;
  productGroupTotals: Record<string, number>;
  productsQuantities: Record<string, number>;
  showWholesalePrice: boolean;
}>();

const emit = defineEmits(['remove-product-group-quantities', 'matrix-input-change', 'show-product-model-details']);

const { t } = useI18n();
const { isASalesRep } = useUserStore();

const collapsed = ref(false);
const selectedSize = ref(props.defaultSize as keyof ProductSize);
const originalQuantities = ref<Record<string, number>>(props.productsQuantities);

const filteredSizeTypes = computed(() =>
  globalConstants.sizeTypes.filter((size) => size !== globalConstants.alwaysVisibleSizeType),
);

const availabilityText = computed(() => ({
  [Availability.outOfStock]: t('config.no_availability_label'),
  [Availability.veryLowStock]: t('config.very_low_availability_label'),
  [Availability.lowStock]: t('config.low_availability_label'),
  [Availability.inStock]: t('config.in_availability_label'),
}));

const fabricDisabilityText = computed(() => ({
  [FabricDisabledReason.launchDateAfterOrderDate]: t('matrix.pre_order_phase_in_date_disabled'),
  [FabricDisabledReason.carryOverRestricted]: t('matrix.pre_order_carry_over_disabled'),
}));

const productGroupTotal = computed(() => props.productGroupTotals[props.productGroup.id] ?? 0);

const sortedProductGroupSizes = computed(() => sortProductSizes(props.productGroup.sizes.values()));

const headerStickyOffset = useStickyHeight([
  '#addEditNavBar',
  '#matrixRelatedOrdersSelector',
  '#quantitiesNavBar',
  '.product-group-header',
]);

const getProductInputValue = (productId: string): number | null => {
  return props.productsQuantities[productId];
};

const getMaxQuantity = (productId: string) => {
  if (props.isQuantityIncreaseAllowed) {
    return MAX_QUANTITY_PER_PRODUCT;
  }

  return originalQuantities.value[productId] ?? 0;
};

const onMatrixInputChange = (product: MatrixProduct, event: Event) => {
  const input = event.target as HTMLInputElement;
  const quantity = parseInt(input.value, 10) || 0;
  const minInput = parseInt(input.min, 10);
  const maxInput = parseInt(input.max, 10);

  const clamped = Math.max(minInput, Math.min(quantity, maxInput));
  input.value = String(clamped);

  emit('matrix-input-change', product, clamped);
};
</script>

<style lang="scss" scoped>
.product-group-matrix {
  .matrix-table {
    max-width: 100%;
    display: block;

    .row {
      display: flex;
      width: 100%;
      max-width: 100%;
      border-bottom: 1px solid var(--border-color-new);
      height: 70px;
      box-sizing: content-box;
      overflow: hidden;

      &.header {
        position: sticky;
        z-index: 1;
        background: var(--primary-bg-color);

        .first-column {
          justify-content: flex-end;

          .size-selector-cnt {
            height: 100%;
            border-right: 1px solid var(--border-color-new);
            margin-right: 81px;

            .size-selector {
              text-align: left;
              background-color: var(--field-bg-color);
              margin: 6px 18px 0 0;
              text-transform: uppercase;
              padding: 8px 0px;
              border-radius: 4px;

              .allways-size {
                padding-left: 16px;
                color: var(--primary-text-remark-color);
                font-size: 12px;
                word-break: keep-all;
              }

              select {
                font-size: 14px;
                font-weight: 500;
                height: auto;
                padding: 5px 55px 5px 15px;
                background-color: var(--field-bg-color);
                color: var(--primary-text-color);
                text-transform: uppercase;
                cursor: pointer;
                border: none;
              }
            }
          }
        }

        .last-column {
          background-color: initial;
        }
      }

      @include breakpoint(medium) {
        height: auto;

        &.row .first-column {
          .size-selector-cnt {
            margin-right: 0;
            border-right: 0;
            height: auto;

            .size-selector {
              margin-right: 0px;
              margin-top: 0px;

              select {
                padding: 5px 19px 5px 15px;
                background-position: right calc(15px - 1em) center;
              }
            }
          }
        }
      }

      &.pre-order-disabled {
        position: relative;

        .column {
          opacity: 0.3;
        }

        .pre-order-disabled-legend {
          width: 100%;
          position: absolute;
          opacity: 1;
          line-height: 100%;
          text-align: center;
          padding: 0 10px;
          color: var(--primary-text-remark-color);
          height: 70px;
          display: flex;
          align-items: center;

          p {
            margin: 0 auto;
            font-weight: 500;
            background: rgba(255, 255, 255, 0.75);
            width: 400px;
            font-size: 0.9em;
          }
        }
      }
    }

    .column {
      width: 100%;
      padding: 0;
      border-left: 1px solid var(--border-color-new);
      display: flex;
      position: relative;
      flex-wrap: wrap;
      align-content: center;

      &.first-column {
        display: flex;
        min-width: 360px;
        width: 360px;
        padding: 0;
        align-items: center;
        flex-flow: row;
      }

      &.last-column {
        min-width: 100px;
        width: 100px;
        border-left: none;
        background-color: var(--bg-remark-color);

        span.strong {
          line-height: 70px;
          display: block;
          width: 100%;
        }
      }

      .product {
        width: 100%;

        .product-header {
          width: 100%;
          height: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          top: 0;
          position: absolute;
          color: var(--primary-text-color);
          background-color: var(--special-gray-color);
          font-size: clamp(7px, 0.55vw, 10px);
          text-transform: uppercase;
          letter-spacing: -0.02em;
          font-family: 'On Semi Mono', monospace;
          padding-top: 1px;

          &.in-stock {
            background-color: var(--special-green-color);
          }
          &.low-stock {
            background-color: var(--special-orange-color);
          }
          &.very-low-stock {
            background-color: var(--special-red-color);
          }
          &.out-of-stock {
            background-color: var(--red-color);
          }

          span {
            overflow: hidden;
            padding: 0 2px;
          }

          + .product-total {
            height: 54px;
            margin-top: 16px;
          }
        }

        .product-total {
          height: 70px;
          width: 100%;

          input {
            padding: 0;

            &:focus::placeholder {
              color: transparent;
            }
          }
        }
      }

      .size-cnt {
        width: 100%;

        div {
          margin-bottom: 5px;
          font-size: 16px;
          font-weight: 700;

          &:last-child {
            margin-bottom: 0;
          }

          &.always-visible-size-type {
            font-weight: 400;
            font-size: 14px;
            color: var(--primary-text-remark-color);
          }
        }
      }

      input[type='number'] {
        border: none;
        width: 100%;
        text-align: center;
        outline-width: 0;
        color: var(--primary-text-color);
        font-size: 16px;
        min-height: 100%;
        box-sizing: border-box;
      }

      @media (max-width: 1000px) {
        &.first-column {
          min-width: 245px;
          width: 245px;
        }

        &.last-column {
          min-width: 55px;
          width: 55px;
        }

        input[type='number'] {
          font-size: 12px;
        }
      }

      @include breakpoint(medium) {
        &.first-column {
          min-width: 80px;
          width: 80px;
          display: block;
        }

        &.last-column {
          min-width: 35px;
          width: 35px;
        }

        .size-cnt {
          div {
            font-size: 10px;

            &.always-visible-size-type {
              font-size: 9px;
            }
          }
        }
      }
    }
  }
}
</style>
